import { Box, Button, Card, CardContent, FormHelperText, Grid, MenuItem } from "@mui/material"
import { useFormik } from "formik"
import { FC, useCallback } from "react"
import * as Yup from 'yup'
import { useHasPermit } from "~/authProvider/useHasPermit"
import { ORGANIZATION_USERS } from "~/constants"
import { SaveAction } from "~/dataProvider/types"
import { OrganizationProfileInput } from "~/gen/api/user"
import UsStates from "~/utils/usStates"
import OdForm, { OnSubmit } from "../formik/OdForm"
import TextField from "../formik/TextField"


interface FormProps {
  save: SaveAction<OrganizationProfileInput>;
  record?: OrganizationProfileInput;
}

const validationSchema = 
  Yup.object().shape({
    name:   Yup.string().max(255).required('Organization Name is required'),
    planId: Yup.string().max(255),
    streetAddress: Yup.string().max(255).required('Street Address is required'),
    address2: Yup.string().max(255),
    state: Yup.string().max(100).required('State is required'),
    city:  Yup.string().max(100).required('City is required'),
    zipCode: Yup.string().max(50).required('Zip Code is required'),
    invoiceEmail: Yup.string().max(100).email().required('Invoice Email Address is required'),
  })

const OrganizationProfileForm: FC<FormProps> = ({ save, record }) => {
  const hasWritePermit = useHasPermit([ORGANIZATION_USERS])
  const initialValues = {
    name: record?.name || '',
    planId: record?.planId || '',
    streetAddress: record?.streetAddress || '',
    address2: record?.address2 || '',
    // country: '',
    state: record?.state || '',
    city: record?.city || '',
    zipCode: record?.zipCode || '',
    invoiceEmail: record?.invoiceEmail || '',
    submit: null
  }
  
  const onSubmit: OnSubmit<typeof initialValues> = useCallback((values, {
    setErrors,
    setStatus,
    setSubmitting,
  }) => {
    if (!hasWritePermit) { return }
    const options = {
      onSuccess: () => {
        setStatus({ success: true })
        setSubmitting(false)
      },
      onFailure: (err: any) => {
        setStatus({ success: false })
        setErrors({ submit: err.message })
        setSubmitting(false)
      }
    }

    save({...values, planId: 1}, '', options)
  }, [save, hasWritePermit])
  const formikCtrl = useFormik({ initialValues, onSubmit, validationSchema, enableReinitialize: true })
  const { errors, isSubmitting } = formikCtrl

  return (
    <OdForm formikCtrl={formikCtrl}>
      <Card>
        <CardContent>
          <Grid container spacing={4} >
            <Grid item md={6} xs={12} >
              <TextField label="Organization Name*" name="name" disabled={!hasWritePermit} />
            </Grid>
            <Grid item md={6} xs={12} >
              <TextField label="Street Address*" name="streetAddress" disabled={!hasWritePermit} />
            </Grid>
            <Grid item md={6} xs={12} >
              <TextField label="Address 2" name="address2" disabled={!hasWritePermit} />
            </Grid>
            <Grid item md={6} xs={12} >
              <TextField label="City*" name="city" disabled={!hasWritePermit} />
            </Grid>
            <Grid item md={6} xs={12} >
              <TextField label="State*" name="state" disabled={!hasWritePermit} select>
                {[
                  <MenuItem disabled value="" key="">None</MenuItem>,
                  ...UsStates.map((stateName) => (
                    <MenuItem
                    key={stateName}
                    value={stateName}
                    >
                    {stateName}
                    </MenuItem>
                  ))
                ]}
              </TextField>

            </Grid>
            <Grid item md={6} xs={12} >
              <TextField label="Zip Code*" name="zipCode" disabled={!hasWritePermit} />
            </Grid>
            {/* <Grid item md={6} xs={12} >
              <TextField
                error={Boolean(touched.planId && errors.planId)} 
                fullWidth
                helperText={touched.planId && errors.planId}
                label="Data Management Plan"
                name="planId"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.planId}
                variant="outlined"
              />
            </Grid> */}
            <Grid item md={6} xs={12} >
              <TextField label="Invoice Email Address*" name="invoiceEmail" disabled={!hasWritePermit} />
            </Grid>
          </Grid>
          {errors.submit && (
            <Box sx={{ mt: 3 }}>
              <FormHelperText error>
                {errors.submit}
              </FormHelperText>
            </Box>
          )}
        </CardContent>
        <Box sx={{ p: 2 }} >
          <Button
            color="primary"
            disabled={!hasWritePermit && isSubmitting}
            type="submit"
            variant="contained"
          >
            Update Organization Profile
          </Button>
        </Box>
      </Card>
    </OdForm>
    
  )
}

export default OrganizationProfileForm;
